import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext(null);

const STORAGE_KEY = 'fq_user_location';

export const LocationProvider = ({ children }) => {
  const [location, setLocation] = useState(null); // { city, country, displayName, lat, lng }
  const [loading, setLoading] = useState(true);
  const [permissionState, setPermissionState] = useState('prompt'); // 'prompt' | 'granted' | 'denied' | 'manual'

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setLocation(parsed);
        setPermissionState('granted');
      } catch { /* ignore */ }
    }
    setLoading(false);
  }, []);

  // Reverse geocode lat/lng → city, country
  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      const city =
        data.address?.city ||
        data.address?.town ||
        data.address?.village ||
        data.address?.county ||
        'Unknown City';
      const country = data.address?.country || '';
      return { city, country, displayName: `${city}${country ? ', ' + country : ''}`, lat, lng };
    } catch {
      return { city: 'Your City', country: '', displayName: 'Your Location', lat, lng };
    }
  };

  const requestGPS = useCallback(async () => {
    if (!navigator.geolocation) {
      setPermissionState('denied');
      return false;
    }
    setLoading(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const loc = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          setLocation(loc);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
          setPermissionState('granted');
          setLoading(false);
          resolve(true);
        },
        async (err) => {
          console.warn('GPS error:', err.code, err.message);
          // Fallback: try IP-based geolocation
          try {
            const res = await fetch('https://ipapi.co/json/');
            if (res.ok) {
              const data = await res.json();
              if (data.city) {
                const loc = {
                  city: data.city,
                  country: data.country_name || '',
                  displayName: `${data.city}, ${data.region || data.country_name || ''}`.trim(),
                  lat: data.latitude,
                  lng: data.longitude,
                };
                setLocation(loc);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
                setPermissionState('granted');
                setLoading(false);
                resolve(true);
                return;
              }
            }
          } catch { /* IP fallback failed too */ }
          setPermissionState('denied');
          setLoading(false);
          resolve(false);
        },
        { timeout: 15000, enableHighAccuracy: false, maximumAge: 300000 }
      );
    });
  }, []);

  const setManualLocation = useCallback((cityName) => {
    if (!cityName.trim()) return;
    const loc = {
      city: cityName.trim(),
      country: '',
      displayName: cityName.trim(),
      lat: null,
      lng: null,
    };
    setLocation(loc);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    setPermissionState('manual');
  }, []);

  // Set a fully geocoded location (from LocationSearchInput autocomplete)
  const setGeocodedLocation = useCallback((locObj) => {
    if (!locObj?.displayName) return;
    setLocation(locObj);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(locObj));
    setPermissionState('granted');
    // Pre-warm home feed cache in background
    prewarmCache(locObj.displayName);
  }, []);

  const prewarmCache = (locationName) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    if (!API_URL || !locationName) return;
    // Fire-and-forget: prime the most common recommendation combos
    setTimeout(() => {
      fetch(`${API_URL}/api/recommendations/quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comfort: 'dine-out', vibe: 'all', cuisine: 'all', dietary: 'all', location: locationName }),
      }).catch(() => {});
    }, 500);
    setTimeout(() => {
      fetch(`${API_URL}/api/home/foodie-feed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: locationName }),
      }).catch(() => {});
    }, 1500);
  };

  const clearLocation = useCallback(() => {
    setLocation(null);
    localStorage.removeItem(STORAGE_KEY);
    setPermissionState('prompt');
  }, []);

  const locationString = location?.displayName || '';

  return (
    <LocationContext.Provider value={{
      location,
      locationString,
      loading,
      permissionState,
      requestGPS,
      setManualLocation,
      setGeocodedLocation,
      clearLocation,
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocation must be used within LocationProvider');
  return ctx;
};
